import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CONFIRM_PHRASE,
  executeStoreOwnershipChange,
  fetchRomOptions,
  fetchStoreOwnershipPreview,
  getApiErrorMessage,
  isValidExternalId,
  searchStores,
  type ExecuteResponse,
  type PreviewResponse,
  type RomOption,
  type StoreSummary,
} from '@/lib/spar-mobile/storeOwnershipChange';

export function useStoreOwnershipChangePanel(canWrite: boolean) {
  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [storeSuggestions, setStoreSuggestions] = useState<StoreSummary[]>([]);
  const [storeSearchLoading, setStoreSearchLoading] = useState(false);
  const [selectedOldStore, setSelectedOldStore] = useState<StoreSummary | null>(
    null,
  );
  const latestSearchRef = useRef(0);

  const latestPreviewRef = useRef(0);
  const submittingRef = useRef(false);
  const requestRef = useRef<{ payload: string; key: string } | null>(null);
  const [discoveryError, setDiscoveryError] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [externalId, setExternalId] = useState('');
  const [newStoreName, setNewStoreName] = useState('');
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmailAddress, setContactEmailAddress] = useState('');
  const [newRomId, setNewRomId] = useState<string | null>(null);
  const [romPopoverOpen, setRomPopoverOpen] = useState(false);
  const [romSearchTerm, setRomSearchTerm] = useState('');

  const [romOptions, setRomOptions] = useState<RomOption[]>([]);
  const [romsLoading, setRomsLoading] = useState(false);
  const romsLoadedRef = useRef(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<ExecuteResponse['data'] | null>(null);

  const externalIdValid = isValidExternalId(externalId);
  const formValid =
    canWrite &&
    !!selectedOldStore &&
    externalIdValid &&
    !!newStoreName.trim() &&
    newStoreName.length <= 255 &&
    contactFirstName.length <= 100 &&
    contactLastName.length <= 100 &&
    newRomId !== null &&
    !previewLoading &&
    !previewError &&
    !!preview?.fingerprint &&
    preview.oldStore.id === selectedOldStore.id &&
    preview.blockers.length === 0;

  const selectedRom = useMemo(
    () => romOptions.find((rom) => rom.id === newRomId) ?? null,
    [romOptions, newRomId],
  );

  const filteredRomOptions = useMemo(() => {
    const trimmed = romSearchTerm.trim().toLowerCase();
    if (!trimmed) return romOptions;
    return romOptions.filter((rom) => {
      const haystack = [rom.name ?? '', rom.dc_name ?? '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [romOptions, romSearchTerm]);

  const resetForm = useCallback(() => {
    setExternalId('');
    setNewStoreName('');
    setContactFirstName('');
    setContactLastName('');
    setContactEmailAddress('');
    setNewRomId(null);
    setRomSearchTerm('');
  }, []);

  const ensureRoms = useCallback(async () => {
    if (romsLoadedRef.current) return;
    romsLoadedRef.current = true;
    setRomsLoading(true);
    setDiscoveryError('');
    try {
      const data = await fetchRomOptions();
      setRomOptions(data);
      romsLoadedRef.current = true;
    } catch (err) {
      romsLoadedRef.current = false;
      setDiscoveryError(getApiErrorMessage(err, 'Failed to load ROM options'));
    } finally {
      setRomsLoading(false);
    }
  }, []);

  useEffect(() => {
    void ensureRoms();
  }, [ensureRoms]);

  useEffect(() => {
    latestSearchRef.current += 1;
    const trimmed = storeSearchTerm.trim();
    if (trimmed.length < 2) {
      setStoreSuggestions([]);
      setStoreSearchLoading(false);
      return;
    }

    latestSearchRef.current += 1;
    const searchId = latestSearchRef.current;
    setStoreSearchLoading(true);

    const handle = setTimeout(async () => {
      try {
        const rows = await searchStores(trimmed);
        if (searchId !== latestSearchRef.current) return;
        setStoreSuggestions(rows);
      } catch (err) {
        if (searchId !== latestSearchRef.current) return;
        setDiscoveryError(getApiErrorMessage(err, 'Failed to search stores'));
        setStoreSuggestions([]);
      } finally {
        if (searchId === latestSearchRef.current) {
          setStoreSearchLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [storeSearchTerm]);

  const loadPreview = useCallback(async (storeId: string) => {
    const previewId = ++latestPreviewRef.current;
    setPreviewLoading(true);
    setPreviewError('');
    setPreview(null);
    try {
      const data = await fetchStoreOwnershipPreview(storeId);
      if (previewId !== latestPreviewRef.current) return;
      setPreview(data);
      if (!data)
        setPreviewError(
          'No current preview was returned. Refresh to try again.',
        );
    } catch (err) {
      if (previewId !== latestPreviewRef.current) return;
      setPreviewError(getApiErrorMessage(err, 'Failed to load store preview'));
    } finally {
      if (previewId === latestPreviewRef.current) setPreviewLoading(false);
    }
  }, []);

  const handleSelectStore = useCallback(
    (store: StoreSummary) => {
      setSelectedOldStore(store);
      setNewStoreName(store.name);
      setStoreSearchTerm('');
      setStoreSuggestions([]);
      setResult(null);
      setSubmitError('');
      void loadPreview(store.id);
    },
    [loadPreview],
  );

  const handleClearStore = useCallback(() => {
    latestPreviewRef.current += 1;
    setPreviewLoading(false);
    setSelectedOldStore(null);
    setPreview(null);
    setPreviewError('');
    setResult(null);
    setSubmitError('');
    resetForm();
  }, [resetForm]);

  const openConfirm = useCallback(() => {
    if (!formValid || submittingRef.current) return;
    setConfirmText('');
    setSubmitError('');
    setConfirmOpen(true);
  }, [formValid]);

  const handleSubmit = useCallback(async () => {
    if (
      !selectedOldStore ||
      !formValid ||
      !preview ||
      submittingRef.current ||
      confirmText !== CONFIRM_PHRASE
    )
      return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    try {
      const body = {
        previewFingerprint: preview.fingerprint,
        oldStoreId: selectedOldStore.id,
        newRomId,
        externalId: externalId.trim(),
        newStoreName: newStoreName.trim(),
        contactFirstName: contactFirstName.trim() || undefined,
        contactLastName: contactLastName.trim() || undefined,
        contactEmailAddress: contactEmailAddress.trim() || undefined,
      };
      const payload = JSON.stringify(body);
      if (requestRef.current?.payload !== payload) {
        requestRef.current = { payload, key: crypto.randomUUID() };
      }
      const data = await executeStoreOwnershipChange({
        ...body,
        idempotencyKey: requestRef.current.key,
      });
      requestRef.current = null;
      setResult(data);
      setConfirmOpen(false);
      setSelectedOldStore(null);
      setPreview(null);
      resetForm();
    } catch (err) {
      setSubmitError(
        getApiErrorMessage(err, 'Failed to execute store ownership change'),
      );
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        setConfirmOpen(false);
        setConfirmText('');
        await loadPreview(selectedOldStore.id);
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    selectedOldStore,
    formValid,
    preview,
    confirmText,
    loadPreview,
    newRomId,
    externalId,
    newStoreName,
    contactFirstName,
    contactLastName,
    contactEmailAddress,
    resetForm,
  ]);

  return {
    discoveryError,
    retryRoms: ensureRoms,
    refreshPreview: () => selectedOldStore && loadPreview(selectedOldStore.id),
    storeSearchTerm,
    setStoreSearchTerm,
    storeSuggestions,
    storeSearchLoading,
    selectedOldStore,
    handleSelectStore,
    handleClearStore,
    preview,
    previewLoading,
    previewError,
    externalId,
    newStoreName,
    contactFirstName,
    contactLastName,
    setExternalId,
    setNewStoreName,
    setContactFirstName,
    setContactLastName,
    contactEmailAddress,
    setContactEmailAddress,
    newRomId,
    setNewRomId,
    romPopoverOpen,
    setRomPopoverOpen,
    romSearchTerm,
    setRomSearchTerm,
    romOptions,
    romsLoading,
    filteredRomOptions,
    selectedRom,
    externalIdValid,
    formValid,
    confirmOpen,
    setConfirmOpen,
    confirmText,
    setConfirmText,
    submitting,
    submitError,
    result,
    openConfirm,
    handleSubmit,
  };
}
